/*
  Подключение рассылки: доступы к вашему оператору сообщений, под которыми
  Арбитр ЖКХ отправляет сообщения должникам (§7.8.1).

  Учётную запись и имя отправителя оформляете вы на своё юридическое лицо - своих
  отправителей Арбитр ЖКХ не заводит и трафик не перепродаёт. Поэтому форма
  спрашивает то же, что написано в кабинете вашего оператора рассылки, и ничего
  сверх того.

  🔴 Ключ доступа сюда не возвращается никогда - ни при открытии страницы, ни
  после сохранения. Показанный однажды, он попадает в снимок экрана и в историю
  браузера, а украденный означает рассылку от вашего имени и за ваш счёт. Пустое
  поле ключа при заполненных остальных означает «ключ задан и не меняется»; чтобы
  заменить - введите новый.

  Про имя отправителя. Это подпись, которую должник видит вместо номера, и
  регистрируется она у оператора связи по вашим документам. Она обязана быть
  вашей: человек получает требование денег со ссылкой на оплату, и сообщение от
  неизвестного отправителя он справедливо примет за мошенническое.

  Про галочку «не писать название в тексте» (29.09.2026). Она решает, повторять
  ли название вашей организации в самом тексте сообщения. Бесплатное имя
  оператора («SMS Aero») стоит в том же поле, что ваше собственное, и отличить их
  по значению нельзя - поэтому спрашиваем прямо. Цена вопроса - восемь знаков из
  семидесяти на каждом сообщении волны, а за вторую часть оператор берёт как за
  целое сообщение.

  02.10.2026, после вопроса владельца «зачем имя отправителя и тут же галочка».
  Правило подстановки было и раньше - короткое название, без него полное, при
  галочке никакого, - но экран его не показывал, а подпись «Имя отправителя
  моё» описывала признак, а не последствие. Теперь галочка названа тем, что она
  делает, а в пояснении написано, какое именно название встанет в начало
  сообщения. Его присылает сервер тем же правилом, что собирает волну: своё
  правило здесь разошлось бы с настоящим текстом при первой правке. Пояснение -
  под значком «i» за галочкой, как у всех полей (тот же день, указание
  владельца: четыре строки под галочкой делали форму текстом).
*/

import { useState } from 'react'

import { fieldErrors } from '../api/client.js'
import { LockedForm } from './AccessContext.jsx'
import { Check, Field, Tag } from './Common.jsx'
import SmsProviderField from './SmsProviderField.jsx'

const LOGIN = {
  key: 'sms_login',
  label: 'Почта учётной записи',
  width: 'm',
  hint: 'Та, на которую вы зарегистрировались у оператора рассылки.',
}

const SENDER = {
  key: 'sms_sender',
  label: 'Имя отправителя',
  width: 's',
  hint: 'Подпись, которую телефон должника показывает вместо номера в строке «от кого». Без неё оператор сообщение не отправит. Регистрируется у оператора рассылки на вашу организацию; пока своей нет, оператор даёт бесплатную, например «SMS Aero», но организацию она не называет.',
}

const OWN_SENDER_TITLE = 'Имя отправителя - название моей организации, в тексте его не повторять'

/*
  Что встанет в начало сообщения - словами, с настоящим названием. Человек
  решает про галочку, видя последствие, а не угадывая его по названию признака.
*/
function textNameHint({ text_name: name, text_name_is_short: isShort, sms_sender: sender }, own) {
  if (own) {
    return `Название в текст не ставится: должник видит организацию в имени отправителя${
      sender ? ` «${sender}»` : ''
    }. Снимите отметку, если имя отправителя - бесплатная подпись оператора: она организацию не называет, и сообщение с требованием денег примут за мошенническое.`
  }
  const source = isShort
    ? 'Это короткое название с вкладки «Организация».'
    : 'Короткое название на вкладке «Организация» не задано, поэтому стоит наименование целиком: оно занимает много места, и сообщение может уйти двумя частями по двойной цене.'
  return `Каждое сообщение начнётся с «${name}:», чтобы должник знал, кому он должен. ${source} Отметьте, только если имя отправителя - это название вашей организации.`
}

export default function SettingsSmsChannel({ channel, onSave, error }) {
  /*
    Ключ - единственное управляемое поле формы: после удачного сохранения оно
    очищается. Оставленные в нём точки читаются как «ключ показан», хотя это
    всего лишь то, что человек сам ввёл минуту назад. При отказе сервера
    введённое сохраняется - набирать ключ заново из-за чужой ошибки незачем.
  */
  const [keyInput, setKeyInput] = useState('')

  /*
    Галочка управляемая: в `FormData` состояние флажка попадает только когда он
    отмечен, и «сняли галочку» было бы неотличимо от «поля не было». Для
    значения, которое меняет текст всех сообщений волны, такая двусмысленность
    недопустима.
  */
  const [senderIsOwn, setSenderIsOwn] = useState(Boolean(channel.sms_sender_is_own))

  // Выпадающий список управляемый: это кнопка, а не поле, и в `FormData` он не
  // попадает. Внесённое раньше вручную и неподдержанное - «не выбран».
  const providers = channel.providers ?? {}
  const [provider, setProvider] = useState(
    channel.provider in providers ? channel.provider : '',
  )

  /*
    Какое поле сервер не принял и почему. Разбор приходит с сервера: своих правил
    проверки кабинет не заводит, иначе их пришлось бы держать в двух местах, и
    первая же правка схемы их развела бы.
  */
  const fieldError = fieldErrors(error)

  const plain = ({ key, label, width, hint }) => (
    <Field
      key={key}
      label={label}
      width={width}
      hint={hint}
      error={fieldError[key]}
      errorId={`sms-error-${key}`}
    >
      <input
        className="field__input"
        name={key}
        defaultValue={channel[key] ?? ''}
        aria-invalid={fieldError[key] ? 'true' : undefined}
        aria-describedby={fieldError[key] ? `sms-error-${key}` : undefined}
      />
    </Field>
  )

  return (
    <>
      <Readiness channel={channel} />

      <LockedForm
        onSubmit={async (event) => {
          event.preventDefault()
          const form = new FormData(event.currentTarget)
          const key = keyInput.trim()
          const saved = await onSave({
            provider: provider || null,
            sms_login: form.get('sms_login') || null,
            sms_sender: form.get('sms_sender') || null,
            sms_sender_is_own: senderIsOwn,
            // Пустое поле означает «не меняли»: сервер отличает это от
            // «стереть», потому что стирать ключ незачем - рассылку выключают
            // целиком, а не оставляют без ключа.
            sms_api_key: key || null,
          })
          if (saved) setKeyInput('')
        }}
      >
        {/* Две группы, а не одна с заголовком посередине: заголовок между
            полями без отступа читался продолжением поля ключа (осмотр
            02.10.2026). Отступ между группами - штатный, как на «Тарифах». */}
        <fieldset className="group">
          <legend className="group__title">Учётная запись у оператора</legend>
          <SmsProviderField
            value={provider}
            onChange={setProvider}
            providers={providers}
            error={fieldError.provider}
          />
          {plain(LOGIN)}

          <Field
            label="Ключ доступа"
            width="s"
            error={fieldError.sms_api_key}
            errorId="sms-error-sms_api_key"
            hint={
              channel.has_api_key
                ? 'Ключ задан. Оставьте пустым, если меняете только остальное.'
                : 'Возьмите его в кабинете оператора рассылки - раздел с настройками API.'
            }
          >
            <input
              className="field__input"
              name="sms_api_key"
              type="password"
              autoComplete="new-password"
              value={keyInput}
              onChange={(event) => setKeyInput(event.target.value)}
              placeholder={channel.has_api_key ? 'не меняется' : ''}
              aria-invalid={fieldError.sms_api_key ? 'true' : undefined}
              aria-describedby={fieldError.sms_api_key ? 'sms-error-sms_api_key' : undefined}
            />
          </Field>
        </fieldset>

        {/*
          Имя отправителя и галочка стоят одной группой: обе отвечают на вопрос,
          как должник узнает, кто ему пишет, - подписью или названием в тексте.
          Порознь они читались как два независимых «имени» (02.10.2026).
        */}
        <fieldset className="group">
          <legend className="group__title">Как должник узнает, кто пишет</legend>
          {plain(SENDER)}
          <Field
            label="Не писать название организации в тексте"
            width="m"
            className="field--check"
            hint={textNameHint(channel, senderIsOwn)}
          >
            <Check checked={senderIsOwn} onChange={setSenderIsOwn} title={OWN_SENDER_TITLE} />
          </Field>
        </fieldset>

        <button className="button" type="submit">
          Сохранить рассылку
        </button>
      </LockedForm>
    </>
  )
}

/*
  Состояние рассылки: готова ли она отправлять и что мешает.

  В отличие от линии связи, состояние здесь означает ровно «учётная запись
  заполнена». Оператор рассылки о себе не сообщает ничего, пока мы не отправим
  сообщение, и притворяться, что мы знаем больше, нельзя.
*/
function Readiness({ channel }) {
  if (!channel.provider && !channel.sms_login && !channel.has_api_key) {
    return (
      <div className="notice notice--warn">
        <div className="notice__what">Рассылка ещё не подключена.</div>
        <div className="notice__do">
          Заполните поля ниже данными вашего оператора рассылки. Пока рассылка не подключена,
          сообщения должникам не уходят, а волна останавливается до первого сообщения.
        </div>
      </div>
    )
  }

  if (channel.missing.length > 0) {
    return (
      <div className="notice notice--warn">
        <div className="notice__what">Не заполнено: {channel.missing.join(', ')}.</div>
        <div className="notice__do">{channel.instruction}</div>
      </div>
    )
  }

  return (
    <>
      <Tag kind="calm">рассылка подключена</Tag>
      <div className="muted">
        Сообщения уходят от имени «{channel.sms_sender}». Что оператор принял сообщение, видно в
        истории обращений; что человек его прочитал, не знает никто - этого не сообщает ни один
        оператор.
      </div>
    </>
  )
}
